// components/CreditEngineSettings.jsx
import React, { useEffect, useState, useMemo } from "react";
import {
  Button,
  Checkbox,
  Label,
  TextInput,
  Textarea,
  Alert,
  Badge,
} from "flowbite-react";
import {
  HiSparkles,
  HiOutlineShieldCheck,
  HiOutlineExclamationCircle,
} from "react-icons/hi";

const cx = (...c) => c.filter(Boolean).join(" ");

const DEFAULT_CONFIG = {
  autoReject: {
    protesto: false,
    protestoMinValue: "",
    chequeSemFundo: false,
    chequeMinCount: "",
    pendenciaFinanceira: false,
    pendenciaMinValue: "",
  },
  scoreThreshold: "",
  foundationMinDate: "",
  notes: "",
};

export default function CreditEngineSettingsModal({
  userRole = "USER", // "ADMIN" | "POWER_USER" | "USER"
  initialConfig,
  onSave,
  saving = false,
}) {
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const canEdit = useMemo(
    () => ["ADMIN", "POWER_USER"].includes(userRole),
    [userRole]
  );

  useEffect(() => {
    if (initialConfig) {
      setConfig({
        autoReject: {
          protesto: !!initialConfig.autoRejectProtesto ?? false,
          protestoMinValue:
            initialConfig.protestoMinValue != null
              ? String(initialConfig.protestoMinValue)
              : "",
          chequeSemFundo: !!initialConfig.autoRejectChequeSemFundo ?? false,
          chequeMinCount:
            initialConfig.chequeMinCount != null
              ? String(initialConfig.chequeMinCount)
              : "",
          pendenciaFinanceira:
            !!initialConfig.autoRejectPendenciaFinanceira ?? false,
          pendenciaMinValue:
            initialConfig.pendenciaMinValue != null
              ? String(initialConfig.pendenciaMinValue)
              : "",
        },
        scoreThreshold:
          initialConfig.scoreThreshold != null
            ? String(initialConfig.scoreThreshold)
            : "",
        foundationMinDate: initialConfig.foundationMinDate || "",
        notes: initialConfig.notes ?? "",
      });
    } else {
      setConfig(DEFAULT_CONFIG);
    }
  }, [initialConfig]);

  const handleCheck = (field) => (e) => {
    setConfig((prev) => ({
      ...prev,
      autoReject: {
        ...prev.autoReject,
        [field]: e.target.checked,
      },
    }));
  };

  const handleScoreChange = (e) => {
    const { value } = e.target;
    if (value === "" || /^[0-9]+$/.test(value)) {
      setConfig((prev) => ({ ...prev, scoreThreshold: value }));
    }
  };

  const handleProtestoMinValueChange = (e) => {
    const { value } = e.target;
    // permite vazio ou número com vírgula/ponto
    if (value === "" || /^[0-9]+([.,][0-9]{0,2})?$/.test(value)) {
      setConfig((prev) => ({
        ...prev,
        autoReject: {
          ...prev.autoReject,
          protestoMinValue: value,
        },
      }));
    }
  };

  const handleChequeMinCountChange = (e) => {
    const { value } = e.target;
    if (value === "" || /^[0-9]+$/.test(value)) {
      setConfig((prev) => ({
        ...prev,
        autoReject: {
          ...prev.autoReject,
          chequeMinCount: value,
        },
      }));
    }
  };

  const handlePendenciaMinValueChange = (e) => {
    const { value } = e.target;
    if (value === "" || /^[0-9]+([.,][0-9]{0,2})?$/.test(value)) {
      setConfig((prev) => ({
        ...prev,
        autoReject: {
          ...prev.autoReject,
          pendenciaMinValue: value,
        },
      }));
    }
  };

  const handleFoundationMinDateChange = (e) => {
    setConfig((prev) => ({
      ...prev,
      foundationMinDate: e.target.value,
    }));
  };

  const handleNotesChange = (e) => {
    setConfig((prev) => ({ ...prev, notes: e.target.value }));
  };

  const handleSubmit = (e) => {
    if (e) e.preventDefault();
    if (!canEdit) return;

    const toNumber = (raw) => {
      if (raw === "" || raw == null) return null;
      const normalized = String(raw).replace(",", ".");
      const n = Number(normalized);
      return Number.isNaN(n) ? null : n;
    };

    const payload = {
      // flags principais
      autoRejectProtesto: !!config.autoReject.protesto,
      autoRejectChequeSemFundo: !!config.autoReject.chequeSemFundo,
      autoRejectPendenciaFinanceira: !!config.autoReject.pendenciaFinanceira,

      // limites / parâmetros
      protestoMinValue: toNumber(config.autoReject.protestoMinValue),
      chequeMinCount:
        config.autoReject.chequeMinCount === ""
          ? null
          : Number(config.autoReject.chequeMinCount),
      pendenciaMinValue: toNumber(config.autoReject.pendenciaMinValue),

      scoreThreshold:
        config.scoreThreshold === "" ? null : Number(config.scoreThreshold),

      foundationMinDate: config.foundationMinDate || null,
      notes: config.notes || null,
    };

    onSave?.(payload);
  };

  const hasAnyRule =
    config.autoReject.protesto ||
    config.autoReject.chequeSemFundo ||
    config.autoReject.pendenciaFinanceira ||
    !!config.scoreThreshold ||
    !!config.foundationMinDate;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-2xl bg-indigo-500/10 border border-indigo-400/40 flex items-center justify-center">
            <HiOutlineShieldCheck className="w-6 h-6 text-indigo-500" />
          </div>
          <div>
            <h1 className="text-xl md:text-2xl font-semibold text-slate-900">
              Motor de Crédito
            </h1>
            <p className="text-sm text-slate-500">
              Configure regras detalhadas de reprovação automática com base em
              protestos, cheques, pendências, score e tempo de fundação.
            </p>
          </div>
        </div>
      </div>

      {!canEdit && (
        <Alert color="warning" icon={HiOutlineExclamationCircle}>
          <span className="font-medium">Permissão limitada.</span> Você não pode
          editar o motor de crédito. Fale com um usuário ADMIN ou POWER_USER
          para alterar as regras.
        </Alert>
      )}

      <form
        onSubmit={handleSubmit}
        className="grid grid-cols-1 xl:grid-cols-3 gap-6 items-start"
      >
        {/* Coluna esquerda: Regras */}
        <div className="xl:col-span-2 space-y-4">
          <div className="rounded-2xl border border-slate-200 bg-white p-4 md:p-5 shadow-sm">
            <div className="flex items-center justify-between gap-2 mb-3">
              <div>
                <h2 className="text-base md:text-lg font-semibold text-slate-900">
                  Regras de reprovação automática
                </h2>
                <p className="text-xs md:text-sm text-slate-500">
                  Defina em quais situações a proposta deve ser reprovada sem
                  análise manual e quais limites disparam cada regra.
                </p>
              </div>
              <HiSparkles className="hidden md:block w-5 h-5 text-indigo-400" />
            </div>

            <div className="space-y-3">
              <RuleCheckbox
                label="Reprovar por protestos"
                description="Você pode definir um valor mínimo total de protestos para disparar a reprovação automática."
                checked={config.autoReject.protesto}
                onChange={handleCheck("protesto")}
                disabled={!canEdit}
              >
                <div className="grid grid-cols-1  gap-3 mt-2">
                  <div>
                    <Label
                      htmlFor="protestoMinValue"
                      value="Reprovar se valor total de protestos for a partir de"
                      className="text-xs font-medium text-slate-700"
                    />
                    <div className="mt-1 max-w-xs">
                      <TextInput
                        id="protestoMinValue"
                        type="text"
                        placeholder="ex: 1000,00"
                        value={config.autoReject.protestoMinValue}
                        onChange={handleProtestoMinValueChange}
                        disabled={!canEdit || !config.autoReject.protesto}
                      />
                    </div>
                    <p className="mt-1 text-[11px] text-slate-500">
                      Deixe em branco para reprovar com qualquer valor de
                      protesto.
                    </p>
                  </div>
                </div>
              </RuleCheckbox>

              <RuleCheckbox
                label="Reprovar por cheques sem fundo"
                description="Controle a partir de quantos cheques devolvidos a proposta deve ser reprovada."
                checked={config.autoReject.chequeSemFundo}
                onChange={handleCheck("chequeSemFundo")}
                disabled={!canEdit}
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
                  <div>
                    <Label
                      htmlFor="chequeMinCount"
                      value="Reprovar se houver a partir de"
                      className="text-xs font-medium text-slate-700"
                    />
                    <div className="mt-1 max-w-xs flex items-center gap-2">
                      <TextInput
                        id="chequeMinCount"
                        type="number"
                        min={0}
                        placeholder="ex: 1, 2, 3..."
                        value={config.autoReject.chequeMinCount}
                        onChange={handleChequeMinCountChange}
                        disabled={!canEdit || !config.autoReject.chequeSemFundo}
                      />
                      <span className="text-xs text-slate-600 shrink-0">
                        cheques
                      </span>
                    </div>
                    <p className="mt-1 text-[11px] text-slate-500">
                      Deixe em branco para reprovar com qualquer quantidade de
                      cheques sem fundo.
                    </p>
                  </div>
                </div>
              </RuleCheckbox>

              <RuleCheckbox
                label="Reprovar por pendências financeiras"
                description="Defina um valor mínimo total de pendências para reprovar automaticamente."
                checked={config.autoReject.pendenciaFinanceira}
                onChange={handleCheck("pendenciaFinanceira")}
                disabled={!canEdit}
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
                  <div>
                    <Label
                      htmlFor="pendenciaMinValue"
                      value="Reprovar se valor total de pendências for a partir de"
                      className="text-xs font-medium text-slate-700"
                    />
                    <div className="mt-1 max-w-xs">
                      <TextInput
                        id="pendenciaMinValue"
                        type="text"
                        placeholder="ex: 500,00"
                        value={config.autoReject.pendenciaMinValue}
                        onChange={handlePendenciaMinValueChange}
                        disabled={
                          !canEdit || !config.autoReject.pendenciaFinanceira
                        }
                      />
                    </div>
                    <p className="mt-1 text-[11px] text-slate-500">
                      Deixe em branco para reprovar com qualquer valor de
                      pendência.
                    </p>
                  </div>
                </div>
              </RuleCheckbox>
            </div>

            <div className="mt-5 border-t border-slate-100 pt-4 space-y-4">
              <div className="flex flex-col md:flex-row md:items-end md:gap-4 gap-2">
                <div className="flex-1">
                  <Label
                    htmlFor="scoreThreshold"
                    value="Reprovar se score for menor que"
                    className="text-sm font-medium text-slate-800"
                  />
                  <div className="mt-1 max-w-xs">
                    <TextInput
                      id="scoreThreshold"
                      type="number"
                      min={0}
                      max={1000}
                      placeholder="ex: 500"
                      value={config.scoreThreshold}
                      onChange={handleScoreChange}
                      disabled={!canEdit}
                    />
                  </div>
                  <p className="mt-1 text-xs text-slate-500">
                    Deixe em branco para não usar score como critério
                    automático.
                  </p>
                </div>
              </div>

              <div className="flex flex-col md:flex-row md:items-end md:gap-4 gap-2">
                <div className="flex-1">
                  <Label
                    htmlFor="foundationMinDate"
                    value="Reprovar empresas muito novas"
                    className="text-sm font-medium text-slate-800"
                  />
                  <div className="mt-1 max-w-xs">
                    <TextInput
                      id="foundationMinDate"
                      type="date"
                      value={config.foundationMinDate}
                      onChange={handleFoundationMinDateChange}
                      disabled={!canEdit}
                    />
                  </div>
                  <p className="mt-1 text-xs text-slate-500">
                    Se informado, o motor reprova clientes cuja data de fundação
                    seja posterior a esta data (empresa muito recente).
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Coluna direita: Resumo + Observações */}
        <div className="space-y-4">
          <div className="rounded-2xl border border-slate-200 bg-slate-950 text-slate-50 p-4 md:p-5 shadow-sm">
            <h3 className="text-sm font-semibold flex items-center gap-2 mb-2">
              <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-indigo-500">
                <HiOutlineShieldCheck className="w-4 h-4 text-white" />
              </span>
              Resumo do motor de crédito
            </h3>
            <p className="text-xs text-slate-300 mb-3">
              É assim que as suas consultas serão avaliadas automaticamente:
            </p>

            <ul className="space-y-2 text-xs">
              <SummaryItem
                active={config.autoReject.protesto}
                text={
                  config.autoReject.protestoMinValue
                    ? `Reprovar se houver protestos a partir de R$ ${config.autoReject.protestoMinValue}.`
                    : "Reprovar com qualquer valor de protesto."
                }
              />
              <SummaryItem
                active={config.autoReject.chequeSemFundo}
                text={
                  config.autoReject.chequeMinCount
                    ? `Reprovar a partir de ${config.autoReject.chequeMinCount} cheque(s) sem fundo.`
                    : "Reprovar com qualquer quantidade de cheques sem fundo."
                }
              />
              <SummaryItem
                active={config.autoReject.pendenciaFinanceira}
                text={
                  config.autoReject.pendenciaMinValue
                    ? `Reprovar se o valor total de pendências for a partir de R$ ${config.autoReject.pendenciaMinValue}.`
                    : "Reprovar com qualquer valor de pendência financeira."
                }
              />
              <SummaryItem
                active={!!config.scoreThreshold}
                text={
                  config.scoreThreshold
                    ? `Reprovar se o score for menor que ${config.scoreThreshold}.`
                    : "Score não está sendo utilizado como critério automático."
                }
              />
              <SummaryItem
                active={!!config.foundationMinDate}
                text={
                  config.foundationMinDate
                    ? `Reprovar empresas com data de fundação posterior a ${config.foundationMinDate}.`
                    : "Sem restrição por tempo de fundação."
                }
              />
            </ul>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-4 md:p-5 shadow-sm">
            <div className="flex items-center justify-between gap-2 mb-2">
              <h3 className="text-sm font-semibold text-slate-900">
                Observações internas
              </h3>
              <Badge color="gray" size="xs">
                Opcional
              </Badge>
            </div>
            <p className="text-xs text-slate-500 mb-2">
              Registre políticas adicionais, exceções ou orientações para o time
              de análise de crédito. Essas informações podem ser usadas também
              no prompt da sua IA.
            </p>
            <Textarea
              rows={4}
              value={config.notes}
              onChange={handleNotesChange}
              placeholder="Ex: Tolerar pendências pequenas para clientes com mais de 5 anos de relacionamento..."
              disabled={!canEdit}
            />
          </div>
        </div>
      </form>

      {canEdit && (
        <div className="flex justify-center">
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={saving}
            className="px-4 md:px-6"
          >
            {saving ? "Salvando..." : "Salvar configurações"}
          </Button>
        </div>
      )}
    </div>
  );
}

function RuleCheckbox({
  label,
  description,
  checked,
  onChange,
  disabled,
  children,
}) {
  return (
    <label
      className={cx(
        "flex flex-col gap-2 rounded-xl border p-3 transition-all cursor-pointer",
        checked
          ? "border-indigo-500/70 bg-indigo-50/60"
          : "border-slate-200 bg-slate-50/60 hover:border-indigo-300 hover:bg-indigo-50/40",
        disabled && "opacity-60 cursor-not-allowed"
      )}
    >
      <div className="flex items-start gap-3">
        <Checkbox
          checked={checked}
          onChange={onChange}
          disabled={disabled}
          className="mt-0.5"
        />
        <div className="space-y-0.5">
          <p className="text-sm font-medium text-slate-900">{label}</p>
          <p className="text-xs text-slate-500">{description}</p>
        </div>
      </div>

      {children && checked && <div className="pl-7 w-full">{children}</div>}
    </label>
  );
}

function SummaryItem({ active, text }) {
  return (
    <li className="flex items-start gap-2">
      <span
        className={cx(
          "mt-0.5 h-2 min-w-2 block w-2 rounded-full",
          active ? "bg-green-400" : "bg-white/40"
        )}
      />
      <span className="text-slate-100/90">{text}</span>
    </li>
  );
}
